import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { forkJoin, Observable, of } from 'rxjs';
import { first, map, share, switchMap, tap } from 'rxjs/operators';

import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngxs/store';

import { ProfileResponse } from '@smarthead-api/models/profile-response';
import { ProfileToken } from '@smarthead-api/models/profile-token';
import { UserService } from '@smarthead-api/services/user.service';

import { environment } from '../../../environments/environment';
import { ProfileState } from '../../profile/state/profile.state';
import { AngularLocaleService } from '../services/angular-locale.service';
import { ProfileTokensLoad } from '../state/app.actions';
import { AppState, LoggedUser } from '../state/app.state';

/**
 * This HttpInterceptor add JWT token - IdToken (Auth0) - to each request if it is saved in application state
 * (loggedUser.loggedUserToken).
 * Token is added into Authorization HTTP header (Authorization: Bearer ID_TOKEN)
 * ... if token is expired, token will be NOT added to request and will be REMOVED from store
 *
 * This HTTPInterceptor also add JWT Profile token to special header
 * - tokens are not loaded yet or has been invalidated / unloaded
 * - token for profile has been expired
 * All HTTP requests are postponed till profile tokens are loaded - load of profile token is "shared" observable.
 * See:
 * - https://smarthead.atlassian.net/wiki/spaces/STD/pages/2324922497/Frontend+client+security
 * - https://smarthead.atlassian.net/wiki/spaces/STD/pages/1226866725/Client+store+design+architecture
 *
 */
@Injectable()
export class JWTTokenInterceptor implements HttpInterceptor {
  loggedUser$ = this.store.select(AppState.getUser);
  loadedProfile$ = this.store.select(ProfileState.getLoadedProfile) as Observable<ProfileResponse>;
  profileTokens$ = this.store.select(AppState.getProfilesTokens);

  jwtHelper: JwtHelperService = new JwtHelperService();

  loadingTokens$: Observable<ProfileToken[]> | undefined | null;

  constructor(
    private store: Store,
    private httpClient: HttpClient,
    private userService: UserService,
    private angularLocaleService: AngularLocaleService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.loggedUser$ || !this.loadedProfile$ || !this.profileTokens$) {
      return next.handle(request);
    }

    // login url + profile tokens url DO NOT require to have profile tokens
    if (request.url.endsWith('/profile/tokens') || request.url.endsWith('/me')) {
      return this.loggedUser$.pipe(first()).pipe(
        switchMap(loggedUser => {
          const headers: any = {
            'x-language': this.angularLocaleService.locale
          };
          if (loggedUser) {
            headers['x-authorization'] = `Bearer ${loggedUser.idToken}`;
          }
          return next.handle(
            request.clone({
              setHeaders: headers
            })
          );
        })
      );
    }

    // get logged user (for user token) and profile token (for loaded profile)
    return forkJoin([
      this.loggedUser$.pipe(first()),
      this.getProfileToken().pipe(first())
    ]).pipe(
      first(),
      switchMap(
        ([
          loggedUser,
          profileTokenString
        ]) => {
          if (
            request.url.startsWith(environment.api_smarthead_base_path) ||
            request.url.startsWith(environment.api_smarthead_v2_base_path) ||
            request.url.startsWith(environment.api_link_shortener_base_path)
          ) {
            const headers: any = {
              'x-language': this.angularLocaleService.locale
            };
            if (loggedUser && loggedUser.idToken && !this.jwtHelper.isTokenExpired(loggedUser.idToken)) {
              headers['x-authorization'] = `Bearer ${loggedUser.idToken}`;
            }
            if (profileTokenString) {
              headers['x-smarthead-profile-token'] = profileTokenString;
            }

            return next.handle(
              request.clone({
                setHeaders: headers
              })
            );
          } else {
            // 3th party APIs and APIs which do not support SmartHead tokens/security
            return next.handle(request);
          }
        }
      )
    );
  }

  /**
   * Get for loaded profile - profile token.
   *
   * @private
   */
  private getProfileToken(): Observable<string | undefined> {
    if (this.loggedUser$ && this.loadedProfile$ && this.profileTokens$) {
      return forkJoin([
        this.loggedUser$.pipe(first()),
        this.loadedProfile$.pipe(first()),
        this.profileTokens$.pipe(first())
      ]).pipe(
        first(),
        switchMap(
          ([
            loggedUser,
            loadedProfile,
            profileTokens
          ]) => {
            if (!loggedUser?.userData) {
              return of(undefined);
            }

            let profileTokenString: string | undefined;

            if (!profileTokens) {
              return this.reloadProfileTokensAndSelectForProfile(loggedUser, loadedProfile);
            }

            if (loadedProfile && profileTokens) {
              const profileToken = profileTokens.find(pt => pt.id === loadedProfile.id);
              if (profileToken) {
                profileTokenString = profileToken.token;
              } else {
                return this.reloadProfileTokensAndSelectForProfile(loggedUser, loadedProfile);
              }
            }

            // is expired ?
            if (profileTokenString) {
              if (this.jwtHelper.isTokenExpired(profileTokenString)) {
                return this.reloadProfileTokensAndSelectForProfile(loggedUser, loadedProfile);
              }
            }

            return of(profileTokenString);
          }
        )
      );
    } else {
      return of(undefined);
    }
  }

  /**
   * Start reloading of profile tokens.
   *
   * @param loggedUser
   * @param loadedProfile
   * @private
   */
  private reloadProfileTokensAndSelectForProfile(loggedUser: LoggedUser, loadedProfile: ProfileResponse): Observable<string | undefined> {
    let profileTokenString: string | undefined;

    return this.loadTokensFromAPIAsSharedObservable(loggedUser).pipe(
      first(),
      map(newProfileTokens => {
        if (loadedProfile && newProfileTokens) {
          const profileToken = newProfileTokens.find(pt => pt.id === loadedProfile.id);
          if (profileToken) {
            profileTokenString = profileToken.token;
          }
        }
        return profileTokenString;
      }),
      first()
    );
  }

  /**
   * Load of tokens as shared observable via API.
   *
   * @param loggedUser
   * @private
   */
  private loadTokensFromAPIAsSharedObservable(loggedUser: LoggedUser): Observable<ProfileToken[]> {
    if (!this.loadingTokens$) {
      this.loadingTokens$ = this.userService
        .getUserTokens({
          id: loggedUser.userData.id
        })
        .pipe(
          tap(profileTokens => {
            this.loadingTokens$ = null;
            this.store.dispatch(new ProfileTokensLoad(profileTokens));
          }),
          share()
        );
    }

    return this.loadingTokens$;
  }
}
